import { Injectable } from "@angular/core";
import moment from "moment";
import { ToastService } from "./toast.service";
import { LoaderService } from "./loader.service";
import { ToastType } from "src/components/shared-components/enum/enumMisc";
import { FiltersType } from "src/components/shared-components/enum/enumFilters";
import { TableInfo } from "src/components/shared-components/class/general-classes/item-history";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})
export class DateFormatterFilterService {
  private _fromDay: string = "fromDay";
  private _toDay: string = "toDay";
  private _fromDaySolution: string = "fromDaySolution";
  private _toDaySolution: string = "toDaySolution";
  private _fromTime: string = "fromTime";
  private _toTime: string = "toTime";
  private _idAll: number = -1;

  constructor(
    private _toastService: ToastService,
    private _loaderService: LoaderService
  ) { }

  /**
   * Format the data received by the filters
   * so that it can then be sent to the API.
   * @param filterData data received from filters
   */
  public formatValuesFilter(
    filterData: Map<string, string>,
    errorDate: string,
    tableInfo: TableInfo
  ): Map<string, string> {
    let filData = new Map<string, string>();
    let fromDay = filterData.get(this._fromDay);
    let toDay = filterData.get(this._toDay);
    let fromTime = filterData.get(this._fromTime);
    let toTime = filterData.get(this._toTime);
    let fromDate: Date = null;
    let toDate: Date = null;
    if (fromDay) {
      fromDate = new Date(
        fromTime
          ? this.formatDate(fromDay + " " + fromTime, 2, false)
          : this.formatDate(fromDay, 1, false)
      );
      fromDay = fromTime
        ? this.formatDate(fromDay + " " + fromTime, 2, true)
        : this.formatDate(fromDay, 1, true);
      filData.set(this._fromDay, fromDay);
    }
    if (toDay) {
      toDate = new Date(
        toTime
          ? this.formatDate(toDay + " " + toTime + ":59", 2, false)
          : this.formatDate(toDay, 1, false)
      );
      toDay = toTime
        ? this.formatDate(toDay + " " + toTime + ":59", 2, true)
        : this.formatDate(toDay, 1, true);
      filData.set(this._toDay, toDay);
    }
    if (!this.checkDates(fromDate, toDate, errorDate)) {
      return null;
    }
    fromDay = filterData.get(this._fromDaySolution);
    toDay = filterData.get(this._toDaySolution);
    if (fromDay) {
      fromDate = new Date(this.formatDate(fromDay, 1, false));
      fromDay = this.formatDate(fromDay, 1, true);
      filData.set(this._fromDaySolution, fromDay);
    }
    if (toDay) {
      toDate = new Date(this.formatDate(toDay, 1, false));
      toDay = this.formatDate(toDay, 1, true);
      filData.set(this._toDaySolution, toDay);
    }
    if (!this.checkDates(fromDate, toDate, errorDate)) {
      return null;
    }
    filData = this.fillFilterData(filData, filterData);
    if (fromTime) {
      filData.delete(this._fromTime);
    }
    if (toTime) {
      filData.delete(this._toTime);
    }
    let combo = tableInfo.Filters.find((x) => x.Kind === FiltersType.combo);
    if (combo) {
      let value = filterData.get(combo.Name);
      if (value === this._idAll.toString()) {
        filData.delete(combo.Name);
      }
    }
    return filData;
  }

  /**
   * Returns the formatted date.
   * @param date
   * @param type 1: date only, 2: date and time
   * @param before false: date formatted for comparison with others
   */
  private formatDate(date: string, type: number, before: boolean): string {
    let beforeFormat = "";
    let afterFormat = "";
    switch (type) {
      case 1:
        beforeFormat =
          MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_INVERSE;
        afterFormat = before
          ? MomentFormats.MOMENT_DATETIME_FORMAT_SHORT_DATE_STRAIGHT
          : "MM-DD-YYYY";
        break;
      case 2:
        beforeFormat = MomentFormats.MOMENT_DATETIME_FORMAT;
        afterFormat = before
          ? MomentFormats.MOMENT_DATETIME_FORMAT_SHORT
          : "MM-DD-YYYY HH:mm:ss";
        break;
    }
    return moment(date, beforeFormat).format(afterFormat);
  }
  /**
   * If the starting date is greater than the
   * ending date it causes an error toast to appear.
   * @param startDate starting date
   * @param endDate ending date
   */
  private async checkDates(
    startDate: Date,
    endDate: Date,
    errorDate: string
  ): Promise<boolean> {
    if (startDate !== null && endDate !== null) {
      if (startDate > endDate) {
        this._toastService.showToast(ToastType.ERROR, errorDate, 5000);
        await this._loaderService.dismissLoaderWaiting();
        return false;
      }
    }
    return true;
  }
  /**
   * Insert missing values from oldFilter into newFilter.
   * @param newFilter
   * @param oldFilter
   * @returns newFilter
   */
  private fillFilterData(
    newFilter: Map<string, string>,
    oldFilter: Map<string, string>
  ) {
    oldFilter.forEach((value, key) => {
      if (!newFilter.get(key)) {
        newFilter.set(key, value);
      }
    });
    return newFilter;
  }
}
